/**
 * Nachtrag-Version, Betreiberseite im Angebots-Builder (docs/PLAN-NACHTRAG-VERSION.md, Einheit 4).
 *
 * Drei Zustände, wortgetreu nach dem freigegebenen Mockup (Antoine, 30.10.2026):
 *   1. `NachtragErstellen`  - angenommenes Angebot, kein Nachtrag: Grund (nur intern, Pflicht) + "Nachtrag erstellen" (nur Inhaber).
 *   2. `NachtragLaeuft`     - Entwurf in Arbeit ODER versendet und wartend: Banner, Vergleich alt -> neu, Knöpfe.
 *
 * Geld wird hier nie berechnet: Summen kommen aus den Varianten (Server), der Vergleich aus `nachtragsVergleich`
 * (@maestro/pricing, dieselbe Funktion wie auf dem Server); Zeilenunterschiede sind `lineGross` derselben Engine, nur zur Anzeige.
 */
import { useEffect, useState } from "react";
import { lineGross, nachtragsVergleich, type NachtragPositionSnapshot, type NachtragsVergleich } from "@maestro/pricing";
import { apiFetch, toApiError } from "../lib/api";
import { apiMutate } from "../hooks/useApi";
import { Button, Card, Icon, Textarea, useBestaetigung } from "./components";
import { formatDate, formatDateTime, money } from "./format";
import type { OfferItem, OfferOption, OfferOptionDay } from "./types";

const fehlerText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/* ───────────── 0. Ausgleich nach der Zustimmung (Einheit 5, Geld) ───────────── */

interface AusgleichDetails {
  offen?: { version?: number; fehler?: string | null } | null;
  angewendet?: Record<string, { plan?: { faelligCents?: number; restCents?: number; ueberschussCents?: number }; ueberschussCents?: number } | undefined>;
}

/**
 * Ergebnis des automatischen Zahlungsausgleichs der zuletzt angenommenen Fassung (`events.details.nachtragAusgleich`).
 * Zahlen kommen vom Server (Plan aus `nachtragsAusgleich`); der Browser rechnet nichts. Ein offener Ausgleich hat einen
 * Knopf, der denselben idempotenten Weg erneut anstößt (Inhaber).
 */
export function NachtragAusgleichBanner({
  eventId,
  details,
  version,
  istInhaber,
  onVeraendert,
}: {
  eventId: string;
  details: unknown;
  /** Angenommene Fassung. */
  version: number | null;
  istInhaber: boolean;
  onVeraendert: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const a = (details && typeof details === "object" ? (details as Record<string, unknown>).nachtragAusgleich : null) as AusgleichDetails | null;
  if (!a || version == null || version < 2) return null;

  const offen = a.offen && a.offen.version === version ? a.offen : null;
  const eintrag = a.angewendet?.[String(version)];
  if (!offen && !eintrag) return null;

  async function nochmal() {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/nachtrag/ausgleich`, "POST", {});
      onVeraendert();
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  }

  if (offen) {
    return (
      <div role="note" data-testid="nachtrag-ausgleich-offen" className="mb-4 flex items-start gap-2.5 rounded-xl border border-error/40 bg-error-container/30 px-4 py-3 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
        <Icon name="warning" className="mt-0.5 shrink-0 text-[18px]" />
        <div className="min-w-0 flex-1">
          <strong className="block font-bold">Ausgleich offen: Die Zahlungen sind noch nicht an den Nachtrag angepasst.</strong>
          <p className="mt-0.5 text-on-surface-variant dark:text-secondary-fixed-dim">
            Die Zustimmung der Kundin gilt. {offen.fehler ? `Grund: ${offen.fehler} ` : ""}Der Ausgleich wird automatisch wiederholt; du kannst ihn auch jetzt anstoßen.
          </p>
          {fehler && <p className="mt-1 text-error">{fehler}</p>}
          {istInhaber && (
            <div className="mt-2">
              <Button type="button" className="!py-1.5" disabled={busy} onClick={() => void nochmal()} data-testid="nachtrag-ausgleich-nochmal">
                {busy ? "Läuft …" : "Ausgleich erneut versuchen"}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const faellig = eintrag?.plan?.faelligCents ?? 0;
  const rest = eintrag?.plan?.restCents ?? 0;
  const ueberschuss = eintrag?.ueberschussCents ?? eintrag?.plan?.ueberschussCents ?? 0;
  return (
    <div role="note" data-testid="nachtrag-ausgleich" className="mb-4 flex items-start gap-2.5 rounded-xl border border-outline-variant/50 bg-surface-container-low px-4 py-3 font-body-sm text-body-sm text-on-surface dark:border-outline/30 dark:bg-surface-variant/10 dark:text-inverse-on-surface">
      <Icon name="payments" className="mt-0.5 shrink-0 text-[18px]" />
      <div>
        <strong className="block font-bold">
          Ausgleich: {faellig > 0 ? `${money(faellig)} Zahlungslink an die Kundin` : ueberschuss > 0 ? "keine weitere Zahlung nötig" : "Zahlungen unverändert"}
          {rest > 0 ? ` · Rest ${money(rest)} wie vereinbart` : ""}
        </strong>
        {ueberschuss > 0 && (
          <p className="mt-0.5 text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="nachtrag-ausgleich-ueberschuss">
            Überschuss {money(ueberschuss)}: Es wird nichts automatisch erstattet. Bitte in der Schlussrechnung verrechnen oder von Hand erstatten.
          </p>
        )}
      </div>
    </div>
  );
}

/* ───────────── 1. Nachtrag erstellen ───────────── */

export function NachtragErstellen({
  eventId,
  version,
  betragCents,
  istInhaber,
  onErstellt,
  kompakt = false,
}: {
  /** Ohne eigenen Hinweiskopf (im Senden-Dialog steht der Hinweis schon darüber). */
  kompakt?: boolean;
  eventId: string;
  /** Angenommene Fassung N (für "V1"); null, wenn unbekannt. */
  version: number | null;
  betragCents: number | null | undefined;
  istInhaber: boolean;
  /** Optionen-ID des neuen Entwurfs. */
  onErstellt: (optionId: string | null) => void;
}) {
  const [grund, setGrund] = useState("");
  const [fehlt, setFehlt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function erstellen() {
    if (!grund.trim()) {
      setFehlt(true);
      return;
    }
    setBusy(true);
    setFehler(null);
    try {
      const data = await apiMutate<{ option?: { id?: string } }>(`/api/events/${eventId}/nachtrag`, "POST", { grund: grund.trim() });
      onErstellt(data?.option?.id ?? null);
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="nachtrag-erstellen" className="mb-4">
      {!kompakt && (
      <div
        role="note"
        data-testid="builder-gesperrt-hinweis"
        className="flex items-start gap-2.5 rounded-xl border border-outline-variant/50 bg-surface-container-low px-4 py-3 font-body-sm text-body-sm text-on-surface dark:border-outline/30 dark:bg-surface-variant/10"
      >
        <Icon name="lock" className="mt-0.5 shrink-0 text-[18px]" />
        <div>
          <strong className="block font-bold">
            Diese Fassung hat die Kundin verbindlich angenommen{version != null ? ` (V${version}${betragCents != null ? `, ${money(betragCents)}` : ""})` : ""}.
          </strong>
          <p className="mt-0.5 text-on-surface-variant dark:text-secondary-fixed-dim">
            Änderungen direkt im Angebot sind gesperrt. Mit einem Nachtrag bereitest du eine neue Fassung vor, die Kundin muss ihr zustimmen. Bis dahin gilt
            {version != null ? ` V${version}` : " die angenommene Fassung"} unverändert.
          </p>
        </div>
      </div>
      )}
      {istInhaber ? (
        <Card className={kompakt ? "max-w-xl" : "mt-3 max-w-xl"}>
          <h2 className="mb-2.5 font-body-md text-body-md font-semibold text-on-surface dark:text-inverse-on-surface">Nachtrag erstellen</h2>
          <label htmlFor="nachtrag-grund" className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
            Grund (nur intern)
          </label>
          <Textarea
            id="nachtrag-grund"
            rows={4}
            value={grund}
            maxLength={1000}
            aria-invalid={fehlt}
            onChange={(e) => {
              setGrund(e.target.value);
              setFehlt(false);
            }}
            placeholder="z. B. Firma Hinz kommt mit 10 Personen mehr und möchte einen Aperitivo."
            data-testid="nachtrag-grund"
          />
          <p className="mt-1.5 font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">Pflicht. Die Kundin sieht diesen Text nicht.</p>
          {fehlt && (
            <p role="alert" className="mt-1.5 font-body-sm text-[12px] font-semibold text-error" data-testid="nachtrag-grund-fehler">
              Bitte trage einen Grund ein.
            </p>
          )}
          {fehler && (
            <p role="alert" className="mt-1.5 font-body-sm text-[12px] font-semibold text-error" data-testid="nachtrag-erstellen-fehler">
              {fehler}
            </p>
          )}
          <div className="mt-3.5">
            <Button type="button" disabled={busy} onClick={() => void erstellen()} data-testid="nachtrag-erstellen-knopf" className="flex items-center gap-2">
              <Icon name="add" className="text-[18px]" /> {busy ? "Wird erstellt …" : "Nachtrag erstellen"}
            </Button>
          </div>
          <p className="mt-2.5 font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
            Es entsteht eine Kopie von{version != null ? ` V${version}` : " der angenommenen Fassung"} als Entwurf{version != null ? ` V${version + 1}` : ""}.
            {version != null ? ` V${version}` : " Die angenommene Fassung"} bleibt unangetastet.
          </p>
        </Card>
      ) : (
        <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="nachtrag-nur-inhaber">
          Freischalten kann nur der Inhaber.
        </p>
      )}
    </div>
  );
}

/* ───────────── 2. Nachtrag läuft / wartet ───────────── */

interface VollerZustand {
  option: OfferOption;
  items: OfferItem[];
  days: OfferOptionDay[];
}

async function ladeVoll(optionId: string): Promise<VollerZustand> {
  const res = await apiFetch(`/api/offers/${optionId}/full`);
  if (!res.ok) throw await toApiError(res);
  return ((await res.json()) as { data: VollerZustand }).data;
}

const alsPosition = (it: OfferItem): NachtragPositionSnapshot => ({
  id: it.id,
  name: it.name,
  section: it.section,
  dayNo: it.dayNo,
  catalogItemId: it.catalogItemId,
  packageId: it.packageId,
  qtyMilli: it.qtyMilli,
  unitPriceCents: it.unitPriceCents,
  unit: it.unit,
  isOptional: it.isOptional,
});

const ersterTag = (days: OfferOptionDay[]) => [...days].filter((d) => d.date).sort((a, b) => a.dayNo - b.dayNo)[0]?.date ?? null;

/** Betragsunterschied einer Position (neu - alt) in Cent, nur zur Anzeige; null, wenn nicht ermittelbar. */
function positionsDelta(art: "neu" | "entfallen" | "geaendert", name: string, dayNo: number | null, alt: VollerZustand, neu: VollerZustand): number | null {
  const betrag = (st: VollerZustand) => {
    const it = st.items.find((i) => i.name.trim() === name.trim() && i.dayNo === dayNo && !i.isOptional);
    if (!it) return 0;
    const tag = it.dayNo != null ? st.days.find((d) => d.dayNo === it.dayNo)?.guestCount : null;
    try {
      return lineGross(
        { section: it.section, priceMode: it.priceMode, qtyMilli: it.qtyMilli, unitPriceCents: it.unitPriceCents, vatRateBp: it.vatRateBp },
        tag != null && tag > 0 ? tag : st.option.guestCount,
      );
    } catch {
      return null;
    }
  };
  const a = art === "neu" ? 0 : betrag(alt);
  const n = art === "entfallen" ? 0 : betrag(neu);
  return a == null || n == null ? null : n - a;
}

const plusMinus = (c: number) => `${c > 0 ? "+ " : c < 0 ? "− " : ""}${money(Math.abs(c))}`;

function VergleichKarte({
  alt,
  neu,
  gilt,
  eventGuestCount,
  eventDate,
}: {
  alt: VollerZustand;
  neu: VollerZustand;
  /** Angenommene Fassung N (events.accepted_offer_version) - NICHT `option.version`, das ist die Zeilenversion der Variante. */
  gilt: number | null;
  eventGuestCount: number | null | undefined;
  eventDate: string | null | undefined;
}) {
  const v: NachtragsVergleich = nachtragsVergleich(
    { option: { amountTotalCents: alt.option.amountTotalCents, guestCount: alt.option.guestCount, items: alt.items.map(alsPosition) }, event: { guestCount: eventGuestCount, eventDate } },
    { option: { amountTotalCents: neu.option.amountTotalCents, guestCount: neu.option.guestCount, items: neu.items.map(alsPosition) }, event: { guestCount: neu.option.guestCount, eventDate: ersterTag(neu.days) ?? eventDate } },
  );
  const zeile = "flex items-baseline justify-between gap-3 border-t border-outline-variant/40 py-2 first:border-t-0 dark:border-outline/30";
  return (
    <Card data-testid="nachtrag-vergleich">
      <h2 className="mb-2 font-body-md text-body-md font-semibold text-on-surface dark:text-inverse-on-surface">
        Vergleich {gilt != null ? `V${gilt}` : "angenommene Fassung"} → Nachtrag
      </h2>
      {!v.hatAenderungen && <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">Noch keine Änderung gegenüber der angenommenen Fassung.</p>}
      {v.gaeste.geaendert && (
        <div className={zeile} data-testid="nachtrag-vergleich-gaeste">
          <span className="font-body-sm text-body-sm">Gäste</span>
          <span className="whitespace-nowrap font-semibold tabular-nums">
            {v.gaeste.alt ?? "–"} <span className="font-normal text-on-surface-variant">→</span> {v.gaeste.neu ?? "–"}
          </span>
        </div>
      )}
      {v.datum.geaendert && (
        <div className={zeile} data-testid="nachtrag-vergleich-datum">
          <span className="font-body-sm text-body-sm">Datum</span>
          <span className="whitespace-nowrap font-semibold tabular-nums">
            {v.datum.alt ? formatDate(v.datum.alt) : "–"} <span className="font-normal text-on-surface-variant">→</span> {v.datum.neu ? formatDate(v.datum.neu) : "–"}
          </span>
        </div>
      )}
      {v.positionen.map((p, i) => {
        const delta = positionsDelta(p.art, p.name, p.dayNo, alt, neu);
        return (
          <div key={`${p.name}-${i}`} className={zeile} data-testid="nachtrag-vergleich-position" data-art={p.art}>
            <span className="min-w-0 font-body-sm text-body-sm">
              {p.name}
              <span
                className={`ml-1.5 inline-block rounded-md border px-1.5 align-[1px] text-[11px] font-bold leading-[18px] ${
                  p.art === "neu" ? "border-tertiary text-tertiary dark:border-tertiary-fixed-dim dark:text-tertiary-fixed-dim" : p.art === "entfallen" ? "border-error text-error" : "border-on-surface text-on-surface dark:border-inverse-on-surface dark:text-inverse-on-surface"
                }`}
              >
                {p.art === "neu" ? "neu" : p.art === "entfallen" ? "entfällt" : "geändert"}
              </span>
            </span>
            {delta != null && <span className="whitespace-nowrap font-semibold tabular-nums">{plusMinus(delta)}</span>}
          </div>
        );
      })}
      <div className={zeile} data-testid="nachtrag-vergleich-summe">
        <span className="font-body-sm text-body-sm">Summe</span>
        <span className="whitespace-nowrap font-semibold tabular-nums">
          {money(v.summe.altCents)} <span className="font-normal text-on-surface-variant">→</span> {money(v.summe.neuCents)}
        </span>
      </div>
      <div className={`${zeile} font-bold`} data-testid="nachtrag-vergleich-differenz">
        <span className="font-body-sm text-body-sm">Differenz</span>
        <span className="whitespace-nowrap tabular-nums">{plusMinus(v.summe.differenzCents)}</span>
      </div>
    </Card>
  );
}

export function NachtragLaeuft({
  eventId,
  gilt,
  geltendCents,
  naechsteVersion,
  gesendetVersion,
  gesendetAm,
  entwurf,
  istInhaber,
  refreshKey,
  eventGuestCount,
  eventDate,
  onSenden,
  onVeraendert,
}: {
  eventId: string;
  /** Angenommene Fassung N, die bis zur Zustimmung gilt. */
  gilt: number | null;
  geltendCents: number | null | undefined;
  /** Nummer des Entwurfs vor dem Versand (N+1). */
  naechsteVersion: number;
  /** Versendete Fassung N+1; null = noch Entwurf. */
  gesendetVersion: number | null;
  gesendetAm: string | null | undefined;
  entwurf: OfferOption;
  istInhaber: boolean;
  /** Ändert sich nach jedem Speichern; löst den Vergleich neu aus. */
  refreshKey: string;
  eventGuestCount: number | null | undefined;
  eventDate: string | null | undefined;
  onSenden: () => void;
  onVeraendert: () => void;
}) {
  const [stand, setStand] = useState<{ alt: VollerZustand; neu: VollerZustand } | null>(null);
  const [vergleichFehler, setVergleichFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [zurueckgezogen, setZurueckgezogen] = useState(false);
  const { bestaetigen, dialog } = useBestaetigung();
  const vonId = entwurf.nachtragVonOptionId ?? null;
  const versendet = gesendetVersion != null;
  const nummer = gesendetVersion ?? naechsteVersion;

  useEffect(() => {
    if (!vonId) return;
    let aktuell = true;
    void (async () => {
      try {
        const [alt, neu] = await Promise.all([ladeVoll(vonId), ladeVoll(entwurf.id)]);
        if (aktuell) {
          setStand({ alt, neu });
          setVergleichFehler(null);
        }
      } catch (e) {
        if (aktuell) setVergleichFehler(fehlerText(e));
      }
    })();
    return () => {
      aktuell = false;
    };
  }, [vonId, entwurf.id, refreshKey]);

  async function zurueckziehen() {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/nachtrag`, "DELETE");
      setZurueckgezogen(true);
      onVeraendert();
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  }

  async function telefonischZugestimmt() {
    const ok = await bestaetigen({
      titel: `Nachtrag V${nummer} telefonisch bestätigen?`,
      text: `Die Kundin hat dem Nachtrag am Telefon zugestimmt. Ab jetzt gilt V${nummer} verbindlich, die Änderung wird im Protokoll festgehalten.`,
      bestaetigen: "Telefonisch zugestimmt",
    });
    if (!ok) return;
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/nachtrag/telefonisch`, "POST", { entscheidung: "zustimmen" });
      onVeraendert();
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  }

  if (zurueckgezogen) {
    return (
      <p role="status" className="mb-4 font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface" data-testid="nachtrag-zurueckgezogen">
        Nachtrag zurückgezogen.{gilt != null ? ` V${gilt}` : " Die angenommene Fassung"}
        {geltendCents != null ? ` (${money(geltendCents)})` : ""} gilt unverändert.
      </p>
    );
  }

  return (
    <div className="mb-4" data-testid="nachtrag-laeuft" data-zustand={versendet ? "versendet" : "entwurf"}>
      <div
        role="note"
        className="flex items-start gap-2.5 rounded-xl border border-outline-variant/50 bg-surface-container-low px-4 py-3 font-body-sm text-body-sm text-on-surface dark:border-outline/30 dark:bg-surface-variant/10"
      >
        <Icon name={versendet ? "schedule_send" : "edit_note"} className="mt-0.5 shrink-0 text-[18px]" />
        <div className="min-w-0">
          {versendet ? (
            <strong className="block font-bold" data-testid="nachtrag-banner-text">
              Nachtrag V{nummer} versendet{gesendetAm ? ` am ${formatDateTime(gesendetAm)}` : ""} – wartet auf Zustimmung.
            </strong>
          ) : (
            <strong className="block font-bold" data-testid="nachtrag-banner-text">
              Nachtrag V{nummer} in Arbeit – bis zur Zustimmung gilt weiter V{gilt ?? nummer - 1}
              {geltendCents != null ? ` (${money(geltendCents)})` : ""}.
            </strong>
          )}
          <p className="mt-0.5 text-on-surface-variant dark:text-secondary-fixed-dim">
            {versendet
              ? `Bis dahin gilt weiter V${gilt ?? nummer - 1}${geltendCents != null ? ` (${money(geltendCents)})` : ""}.`
              : "Die Kundin sieht den Nachtrag erst, wenn du ihn sendest."}
          </p>
        </div>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {stand ? (
          <VergleichKarte alt={stand.alt} neu={stand.neu} gilt={gilt} eventGuestCount={eventGuestCount} eventDate={eventDate} />
        ) : (
          <Card className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="nachtrag-vergleich-laedt">
            {vergleichFehler ? `Der Vergleich konnte nicht geladen werden: ${vergleichFehler}` : "Vergleich wird geladen …"}
          </Card>
        )}
        {istInhaber ? (
          <Card className="self-start">
            <div className="flex flex-wrap gap-2.5">
              {!versendet && (
                <Button type="button" onClick={onSenden} disabled={busy} className="flex items-center gap-2" data-testid="nachtrag-senden">
                  <Icon name="send" className="text-[18px]" /> Nachtrag senden
                </Button>
              )}
              {versendet && (
                <Button type="button" onClick={() => void telefonischZugestimmt()} disabled={busy} className="flex items-center gap-2" data-testid="nachtrag-telefonisch">
                  <Icon name="call" className="text-[18px]" /> Telefonisch zugestimmt
                </Button>
              )}
              {versendet && (
                <Button type="button" variant="secondary" onClick={onSenden} disabled={busy} data-testid="nachtrag-neu-senden">
                  Nachtrag neu senden
                </Button>
              )}
              <Button type="button" variant="secondary" onClick={() => void zurueckziehen()} disabled={busy} data-testid="nachtrag-zurueckziehen">
                Nachtrag zurückziehen
              </Button>
            </div>
            {fehler && (
              <p role="alert" className="mt-2.5 font-body-sm text-body-sm font-semibold text-error" data-testid="nachtrag-aktion-fehler">
                {fehler}
              </p>
            )}
          </Card>
        ) : (
          <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="nachtrag-nur-inhaber">
            Senden, Zurückziehen und die telefonische Zustimmung zu einem Nachtrag sind Inhabern vorbehalten.
          </p>
        )}
      </div>
      {dialog}
    </div>
  );
}
